import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Lock, Plus, Search } from 'lucide-react';
import { useSnackbar } from 'notistack';

import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';
import { Loading } from '@/shared/components';
import AppPage from '@/shared/components/PageComponents/AppPage';
import PageHeader from '@/shared/components/PageHeader';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { cx } from '@/styles/muiTheme';

import { configRegistryErrorMessage, readConfigRegistryError } from '../hooks/configRegistryError';
import useConfigQuery from '../hooks/useConfigQuery';
import useConfigsQuery, { ConfigSummary } from '../hooks/useConfigsQuery';
import useConfigVersionsQuery from '../hooks/useConfigVersionsQuery';
import useCreateConfigMutation from '../hooks/useCreateConfigMutation';
import useRestoreConfigVersionMutation from '../hooks/useRestoreConfigVersionMutation';
import useUnsavedChangesWarning from '../hooks/useUnsavedChangesWarning';
import useUpdateConfigMutation from '../hooks/useUpdateConfigMutation';
import ConfigDiffReview from './configRegistry/ConfigDiffReview';
import ConfigHistoryPanel from './configRegistry/ConfigHistoryPanel';
import CreateConfigForm from './configRegistry/CreateConfigForm';
import RestoreVersionDialog from './configRegistry/RestoreVersionDialog';
import SchemaForm, { ValuePath } from './configRegistry/SchemaForm';
import UnsavedChangesDialog from './configRegistry/UnsavedChangesDialog';
import { pointerToPath, toErrorMap, validateValue } from './configRegistry/schemaValidation';

/** Renders `format: 'secret'` string fields as password inputs. */
const MASK_SECRETS = true;

/**
 * Every write carries a reason.
 *
 * The design leaves this configurable, but for a registry whose whole point is
 * an audit trail across superadmins, an unexplained version is close to useless
 * a year later - the history list has nothing to show but a number and a date.
 */
const REQUIRE_REASON = true;

const pretty = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * Sentinel for "the pending navigation is into create mode, not to a key".
 *
 * Safe as a sentinel because it is not a legal config key: keys must match
 * /^[A-Z][A-Z0-9_]*$/, so no real row can ever collide with it.
 */
const CREATE_TARGET = '<create>';

const ManageConfigsPage = () => {
  const title = 'Configs';

  useDocumentTitle(title);

  const { sessionState } = useAuth();
  const isAuthReady = isSignedIn(sessionState);
  const snackBar = useSnackbar();

  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [mode, setMode] = useState<'edit' | 'create'>('edit');
  const [tab, setTab] = useState<'form' | 'json' | 'history'>('form');

  const [draft, setDraft] = useState<unknown>(null);
  const [draftText, setDraftText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const [isReviewing, setIsReviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);
  const [restoreVersion, setRestoreVersion] = useState<number | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  /** Server-reported violations, keyed the same way the form keys its rows. */
  const [serverViolations, setServerViolations] = useState<Record<string, string>>({});

  const configsQuery = useConfigsQuery(search);
  const configQuery = useConfigQuery(mode === 'edit' ? selectedKey : null);
  const versionsQuery = useConfigVersionsQuery(
    selectedKey,
    tab === 'history' || restoreVersion != null,
  );

  const updateMutation = useUpdateConfigMutation();
  const createMutation = useCreateConfigMutation();
  const restoreMutation = useRestoreConfigVersionMutation();

  const config = configQuery.data ?? null;

  // Reloads the draft whenever a different config, or a newer version of the
  // same one, arrives. Keyed on version so a successful save resets the editor
  // to what was actually stored rather than to the optimistic local draft.
  useEffect(() => {
    if (!config) return;

    setDraft(config.value);
    setDraftText(config.rawValue);
    setParseError(config.parseError);
    setIsReviewing(false);
    setReason('');
    setServerError(null);
    setServerViolations({});

    // The Form tab is rendered `disabled` for a key it cannot handle, but MUI's
    // `disabled` only stops the click — it does not deselect an already-active
    // tab. Switching from a key that has a schema to one that does not left
    // `tab` on 'form' and rendered the form against a null schema.
    if (!config.hasSchema || config.parseError) {
      setTab((current) => (current === 'form' ? 'json' : current));
    }
  }, [config?.key, config?.version]);

  const savedText = config ? pretty(config.value) : '';
  const isDirty = !!config && !config.parseError && pretty(draft) !== savedText;

  useUnsavedChangesWarning(isDirty);

  const validationErrors = useMemo(() => {
    if (!config?.schema || config.parseError) return [];
    return validateValue(draft, config.schema);
  }, [draft, config?.schema, config?.parseError]);

  // Server violations are merged in alongside the local ones so a rejection
  // lands on the offending field rather than only in a banner. Local errors win
  // a collision: they reflect what is on screen right now.
  const errorMap = useMemo(
    () => ({ ...serverViolations, ...toErrorMap(validationErrors) }),
    [serverViolations, validationErrors],
  );

  const hasErrors = validationErrors.length > 0 || !!parseError;
  const canSave = isDirty && !hasErrors && !config?.isProtected;

  const setValueAt = (path: ValuePath, value: unknown) => {
    if (!path.length) {
      setDraft(value);
      setDraftText(pretty(value));
      setParseError(null);
      return;
    }

    const next = JSON.parse(JSON.stringify(draft ?? {}));
    let cursor = next;

    for (let i = 0; i < path.length - 1; i += 1) {
      const step = path[i];
      if (cursor[step] == null || typeof cursor[step] !== 'object') {
        // Materialise the missing container, choosing its kind from the *next*
        // step: a numeric step means this level has to be an array.
        cursor[step] = typeof path[i + 1] === 'number' ? [] : {};
      }
      cursor = cursor[step];
    }

    cursor[path[path.length - 1]] = value;

    setDraft(next);
    setDraftText(pretty(next));
    setParseError(null);
  };

  const onDraftTextChange = (text: string) => {
    setDraftText(text);

    try {
      setDraft(JSON.parse(text));
      setParseError(null);
    } catch (error) {
      setParseError(`Invalid JSON: ${(error as Error).message}`);
    }
  };

  /** Routes a config switch through the dirty guard. */
  const requestSelect = (key: string) => {
    if (key === selectedKey && mode === 'edit') return;
    if (isDirty) {
      setPendingTarget(key);
      return;
    }

    setSelectedKey(key);
    setMode('edit');
    setExpandedVersion(null);
  };

  const requestCreate = () => {
    if (isDirty) {
      setPendingTarget(CREATE_TARGET);
      return;
    }

    setMode('create');
    setIsReviewing(false);
    setServerError(null);
  };

  const applyPendingTarget = () => {
    const target = pendingTarget;
    setPendingTarget(null);

    if (target === CREATE_TARGET) {
      setMode('create');
      return;
    }

    if (target) {
      setSelectedKey(target);
      setMode('edit');
      setExpandedVersion(null);
    }
  };

  const handleMutationError = (error: unknown, fallback: string) => {
    const body = readConfigRegistryError(error);

    setServerError(configRegistryErrorMessage(error, fallback));
    setServerViolations(
      body?.code === 'SCHEMA_VALIDATION_FAILED' && body.violations
        ? Object.fromEntries(
            body.violations.map((violation) => [pointerToPath(violation.path), violation.message]),
          )
        : {},
    );
  };

  const confirmSave = () => {
    if (!config) return;

    setServerError(null);

    updateMutation.mutate(
      {
        key: config.key,
        value: draftText,
        // `schema` is left off entirely rather than sent as null: the editor
        // never changes a key's contract, and null would clear it.
        note: reason.trim() || null,
        expectedVersion: config.version,
      },
      {
        onSuccess: (saved) => {
          setIsReviewing(false);
          snackBar.enqueueSnackbar(`${saved.key} saved as v${saved.version}`, {
            variant: 'success',
          });
        },
        onError: (error) => handleMutationError(error, 'Failed to save config'),
      },
    );
  };

  const confirmRestore = (version: number, restoreReason: string) => {
    if (!config) return;

    setServerError(null);

    restoreMutation.mutate(
      {
        key: config.key,
        version,
        expectedVersion: config.version,
        note: restoreReason.trim() || null,
      },
      {
        onSuccess: (saved) => {
          setRestoreVersion(null);
          snackBar.enqueueSnackbar(
            `${saved.key} restored from v${version}, now v${saved.version}`,
            { variant: 'success' },
          );
        },
        onError: (error) => handleMutationError(error, 'Failed to restore version'),
      },
    );
  };

  const confirmCreate = (input: {
    key: string;
    value: string;
    schema: unknown;
    note: string | null;
  }) => {
    setServerError(null);

    createMutation.mutate(input, {
      onSuccess: (created) => {
        setMode('edit');
        setSelectedKey(created.key);
        setTab('form');
        snackBar.enqueueSnackbar(`${created.key} created`, { variant: 'success' });
      },
      onError: (error) => handleMutationError(error, 'Failed to create config'),
    });
  };

  if (!isAuthReady || configsQuery.isLoading) {
    return (
      <AppPage title={title} requiredPermissions={['menu:admin:configs']}>
        <Loading />
      </AppPage>
    );
  }

  const configs = configsQuery.data ?? [];

  return (
    <AppPage title={title} requiredPermissions={['menu:admin:configs']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader
          title={title}
          subtitle="Runtime configuration, versioned. Every save is recorded and nothing is ever deleted."
          action={
            <Button
              variant="contained"
              startIcon={<Plus size={14} />}
              onClick={requestCreate}
              sx={{ backgroundColor: cx.blue.main, '&:hover': { backgroundColor: cx.blue.dark } }}
            >
              New config
            </Button>
          }
        />

        <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-start' }}>
          <Stack
            sx={{
              width: 320,
              flexShrink: 0,
              border: `1px solid ${cx.border.subtle}`,
              borderRadius: 1,
              backgroundColor: cx.bg.surface2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ padding: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Filter by key"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={14} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Stack
                direction="row"
                sx={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 1 }}
              >
                <Typography variant="caption" sx={{ color: cx.text.muted }}>
                  {configs.length} key{configs.length === 1 ? '' : 's'}
                </Typography>
                <Chip
                  size="small"
                  label="Delete disabled"
                  sx={{ height: 20, backgroundColor: cx.bg.elevated, color: cx.text.muted }}
                />
              </Stack>
            </Box>

            <Box sx={{ maxHeight: 640, overflow: 'auto' }}>
              {configs.map((entry: ConfigSummary) => {
                const isSelected = entry.key === selectedKey && mode === 'edit';

                return (
                  <Stack
                    key={entry.key}
                    direction="row"
                    onClick={() => requestSelect(entry.key)}
                    sx={{
                      padding: 1.25,
                      gap: 1,
                      cursor: 'pointer',
                      alignItems: 'center',
                      backgroundColor: isSelected ? cx.light[200] : 'transparent',
                      color: isSelected ? cx.text.onLight : cx.text.primary,
                      '&:hover': { backgroundColor: isSelected ? cx.light[200] : cx.bg.elevated },
                    }}
                  >
                    <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: cx.fontMono, fontSize: 12, wordBreak: 'break-all' }}
                      >
                        {entry.key}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: isSelected ? cx.washLight[400] : cx.text.muted }}
                      >
                        v{entry.version} - {entry.updatedAt.slice(0, 10)}
                      </Typography>
                    </Stack>

                    {entry.isProtected && <Lock size={12} />}

                    {isDirty && entry.key === selectedKey && (
                      <Box
                        title="Unsaved changes"
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: cx.gold,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Stack>
                );
              })}

              {!configs.length && (
                <Typography variant="body2" sx={{ color: cx.text.muted, padding: 2 }}>
                  No keys match that filter.
                </Typography>
              )}
            </Box>
          </Stack>

          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              border: `1px solid ${cx.border.subtle}`,
              borderRadius: 1,
              backgroundColor: cx.bg.surface2,
              padding: 2,
            }}
          >
            {mode === 'create' && (
              <CreateConfigForm
                existingKeys={configs.map((entry) => entry.key)}
                requireReason={REQUIRE_REASON}
                isSaving={createMutation.isPending}
                serverError={serverError}
                onCancel={() => setMode('edit')}
                onCreate={confirmCreate}
              />
            )}

            {mode === 'edit' && !config && (
              <Typography variant="body2" sx={{ color: cx.text.muted }}>
                {configQuery.isLoading ? 'Loading...' : 'Select a config from the list.'}
              </Typography>
            )}

            {mode === 'edit' && config && isReviewing && (
              <ConfigDiffReview
                before={savedText}
                after={pretty(draft)}
                reason={reason}
                onReasonChange={setReason}
                requireReason={REQUIRE_REASON}
                nextVersion={config.version + 1}
                onCancel={() => setIsReviewing(false)}
                onConfirm={confirmSave}
                isSaving={updateMutation.isPending}
                serverError={serverError}
              />
            )}

            {mode === 'edit' && config && !isReviewing && (
              <Stack sx={{ gap: 2 }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: cx.fontMono, fontSize: 15, color: cx.text.primary }}
                  >
                    {config.key}
                  </Typography>
                  <Typography variant="caption" sx={{ color: cx.text.muted }}>
                    id {config.id} - v{config.version} - {config.versionCount} version
                    {config.versionCount === 1 ? '' : 's'}
                  </Typography>

                  <Box sx={{ flexGrow: 1 }} />

                  {isDirty && (
                    <Typography variant="caption" sx={{ color: hasErrors ? cx.red.main : cx.gold }}>
                      {hasErrors
                        ? `${validationErrors.length || 1} validation error${
                            (validationErrors.length || 1) === 1 ? '' : 's'
                          }`
                        : 'Unsaved changes'}
                    </Typography>
                  )}

                  <Button
                    size="small"
                    disabled={!isDirty}
                    onClick={() => {
                      setDraft(config.value);
                      setDraftText(config.rawValue);
                      setParseError(config.parseError);
                      setServerViolations({});
                    }}
                    sx={{ color: cx.text.tertiary }}
                  >
                    Discard
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    disabled={!canSave}
                    onClick={() => {
                      setServerError(null);
                      setIsReviewing(true);
                    }}
                    sx={{
                      backgroundColor: canSave ? cx.blue.main : cx.bg.elevated,
                      color: canSave ? cx.text.primary : cx.text.muted,
                      '&:hover': { backgroundColor: canSave ? cx.blue.dark : cx.bg.elevated },
                    }}
                  >
                    Review and save
                  </Button>
                </Stack>

                {config.isProtected && (
                  <Alert severity="info" icon={<Lock size={16} />}>
                    {config.protectedReason ??
                      'This key is read-only. It can be inspected and diffed, but not written.'}
                  </Alert>
                )}

                {config.parseError && (
                  <Alert severity="warning">
                    This value is not JSON ({config.parseError}). It predates the registry, so the
                    form cannot render it. Edit it on the JSON tab, and saving will convert it to a
                    JSON document.
                  </Alert>
                )}

                {!config.hasSchema && !config.parseError && (
                  <Alert severity="info">
                    No schema is set for this key, so it is only checked for being valid JSON.
                  </Alert>
                )}

                <Tabs
                  value={tab}
                  onChange={(_, next) => setTab(next)}
                  sx={{ borderBottom: `1px solid ${cx.border.subtle}`, minHeight: 36 }}
                >
                  <Tab
                    value="form"
                    label="Form"
                    disabled={!config.hasSchema || !!config.parseError}
                  />
                  <Tab value="json" label="JSON" />
                  <Tab value="history" label={`History (${config.versionCount})`} />
                </Tabs>

                {tab === 'form' && (
                  <SchemaForm
                    schema={config.schema as Record<string, any>}
                    value={draft}
                    errors={errorMap}
                    onChange={setValueAt}
                    maskSecrets={MASK_SECRETS}
                    disabled={config.isProtected}
                  />
                )}

                {tab === 'json' && (
                  <Stack sx={{ gap: 1 }}>
                    <TextField
                      multiline
                      minRows={18}
                      fullWidth
                      disabled={config.isProtected}
                      value={draftText}
                      onChange={(event) => onDraftTextChange(event.target.value)}
                      sx={{
                        '& textarea': { fontFamily: cx.fontMono, fontSize: 12 },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: hasErrors ? cx.red.main : cx.border.strong,
                        },
                      }}
                    />
                    {parseError && (
                      <Typography variant="caption" sx={{ color: cx.red.main }}>
                        {parseError}
                      </Typography>
                    )}
                    {validationErrors.map((error) => (
                      <Typography
                        key={`${error.path}:${error.message}`}
                        variant="caption"
                        sx={{ color: cx.red.main }}
                      >
                        <Box component="span" sx={{ fontFamily: cx.fontMono }}>
                          {error.path}
                        </Box>{' '}
                        {error.message}
                      </Typography>
                    ))}
                  </Stack>
                )}

                {tab === 'history' && (
                  <ConfigHistoryPanel
                    configKey={config.key}
                    versions={versionsQuery.data ?? []}
                    isLoading={versionsQuery.isLoading}
                    expandedVersion={expandedVersion}
                    onToggle={setExpandedVersion}
                    onRestore={setRestoreVersion}
                    isProtected={config.isProtected}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Stack>

        <UnsavedChangesDialog
          open={pendingTarget !== null}
          configKey={selectedKey ?? ''}
          onKeepEditing={() => setPendingTarget(null)}
          onDiscard={applyPendingTarget}
        />

        {config && (
          <RestoreVersionDialog
            configKey={config.key}
            version={restoreVersion}
            currentText={savedText}
            currentVersion={config.version}
            requireReason={REQUIRE_REASON}
            isSaving={restoreMutation.isPending}
            serverError={serverError}
            onClose={() => setRestoreVersion(null)}
            onConfirm={confirmRestore}
          />
        )}
      </Container>
    </AppPage>
  );
};

export default ManageConfigsPage;
